/**
 * Approval queue (approver / senior_approver). Lists cases pending approval with their
 * tiered requirement and signatures collected, linking to the case to sign.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, RefreshCw, ChevronRight } from 'lucide-react';
import { api } from '../lib/apiClient.js';
import { StatusBadge } from '../components/ui/StatusBadge.jsx';
import { CATEGORY_META, formatINR } from '../features/cases/caseMeta.js';
import styles from './ApprovalQueue.module.css';

const THRESHOLD = 25000;

export default function ApprovalQueue() {
  const [state, setState] = useState({ status: 'loading', items: [] });

  const load = async () => {
    setState((s) => ({ ...s, status: 'loading' }));
    try {
      const data = await api.get('/cases?status=pending_approval&limit=100');
      setState({ status: 'ready', items: data });
    } catch (err) {
      setState({ status: 'error', error: err.message });
    }
  };
  useEffect(() => {
    load();
  }, []);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="eyebrow">Casework</p>
          <h1 className={styles.h1}>Approval queue</h1>
          <p className={styles.lede}>
            Tiered rule: one approver below {formatINR(THRESHOLD)}, two at or above.
          </p>
        </div>
        <button className={styles.ghost} onClick={load}>
          <RefreshCw size={15} /> Refresh
        </button>
      </header>

      {state.status === 'loading' && (
        <div className={styles.grid}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={styles.skeleton} />
          ))}
        </div>
      )}
      {state.status === 'error' && <p className={styles.error}>{state.error}</p>}
      {state.status === 'ready' && state.items.length === 0 && (
        <div className={styles.empty}>
          <ShieldCheck size={32} />
          <p>Nothing waiting for approval. The queue is clear.</p>
        </div>
      )}

      <div className={styles.grid}>
        {state.status === 'ready' &&
          state.items.map((c) => {
            const required = c.amountRequested >= THRESHOLD ? 2 : 1;
            const Cat = CATEGORY_META[c.category]?.icon;
            return (
              <Link to={`/cases/${c.id}`} key={c.id} className={styles.card}>
                <div className={styles.cardTop}>
                  <span className="mono">{c.caseNo}</span>
                  <StatusBadge status={c.status} />
                </div>
                <strong className={styles.name}>{c.beneficiarySummary?.name}</strong>
                <span className={styles.village}>{c.beneficiarySummary?.village}</span>
                <div className={styles.amount}>
                  {Cat && <Cat size={15} />}
                  <span className="mono">{formatINR(c.amountRequested)}</span>
                </div>
                <div className={styles.foot}>
                  <span className={styles.tier}>
                    {(c.approvals?.length || 0)}/{required} signed
                  </span>
                  <span className={styles.open}>
                    Review <ChevronRight size={14} />
                  </span>
                </div>
              </Link>
            );
          })}
      </div>
    </div>
  );
}
